import React, { useState, useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BookingModal from '../components/BookingModal';
import Seo from '../components/Seo';
import { Button } from '../components/ui/button';
import { Clock, Flame, ChefHat, Users, ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';
import { recipes, getRecipeBySlug } from '../data/recipes';

export const RecipesIndex = () => {
  const [bookOpen, setBookOpen] = useState(false);
  const [tag, setTag] = useState('All');
  const tags = useMemo(() => ['All', ...Array.from(new Set(recipes.map((r) => r.tag)))], []);
  const filtered = tag === 'All' ? recipes : recipes.filter((r) => r.tag === tag);

  return (
    <div className="min-h-screen bg-[#faf7f1]">
      <Seo
        title="Healthy Indian Recipes by NutrifyMe"
        description="Dietitian-curated Indian recipes for weight loss, PCOS, gut health, diabetes and more. Simple, high-protein, low-glycemic and family-friendly."
        keywords={['healthy indian recipes', 'pcos recipes', 'diabetic recipes', 'high protein indian food', 'gut health recipes']}
        path="/recipes"
      />
      <Navbar onBook={() => setBookOpen(true)} />

      <section className="pt-32 pb-14 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#e8f2e2] opacity-60 blur-3xl" />
        <div className="max-w-5xl mx-auto px-6 relative">
          <div className="text-xs tracking-[0.2em] uppercase text-[#c9743a] mb-3">Kitchen</div>
          <h1 className="font-serif text-5xl lg:text-6xl text-[#1a2b17] leading-[1.05]">
            Fuss-free <span className="italic text-[#c9743a] font-light">recipes</span> we love
          </h1>
          <p className="mt-5 text-lg text-[#4a5544] max-w-2xl">
            Every recipe here is created by a real dietitian, tested by real clients, and made with ingredients you probably already have.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {tags.map((t) => (
              <button
                key={t}
                onClick={() => setTag(t)}
                className={`px-4 py-1.5 rounded-full text-sm border transition ${tag === t ? 'bg-[#1a2b17] text-white border-[#1a2b17]' : 'bg-white text-[#1a2b17] border-neutral-200 hover:border-[#1a2b17]'}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((r) => (
            <Link to={`/recipes/${r.slug}`} key={r.slug} className="group relative rounded-3xl overflow-hidden aspect-[4/5] cursor-pointer">
              <img src={r.image} alt={r.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              <div className="absolute top-4 left-4 text-[10px] tracking-widest uppercase text-white bg-white/15 backdrop-blur px-3 py-1 rounded-full">{r.tag}</div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="font-serif text-xl leading-tight">{r.name}</h3>
                <div className="mt-3 flex items-center gap-4 text-xs opacity-90">
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {r.time}</span>
                  <span className="inline-flex items-center gap-1"><Flame className="w-3 h-3" /> {r.calories} kcal</span>
                  <span className="inline-flex items-center gap-1"><ChefHat className="w-3 h-3" /> {r.protein}g P</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Footer onBook={() => setBookOpen(true)} />
      <BookingModal open={bookOpen} onClose={() => setBookOpen(false)} />
    </div>
  );
};

export const RecipeDetail = () => {
  const { slug } = useParams();
  const r = getRecipeBySlug(slug);
  const [bookOpen, setBookOpen] = useState(false);

  if (!r) return <Navigate to="/recipes" replace />;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Recipe',
    name: r.name,
    image: r.image,
    description: r.description,
    recipeYield: `${r.servings} servings`,
    totalTime: r.time,
    recipeCategory: r.tag,
    recipeCuisine: 'Indian',
    nutrition: { '@type': 'NutritionInformation', calories: `${r.calories} kcal`, proteinContent: `${r.protein} g` },
    recipeIngredient: r.ingredients,
    recipeInstructions: r.steps.map((s, i) => ({ '@type': 'HowToStep', position: i + 1, text: s })),
    author: { '@type': 'Person', name: 'Dietitian Prachi' },
  };

  return (
    <div className="min-h-screen bg-[#faf7f1]">
      <Seo
        title={`${r.name} — Healthy Indian Recipe`}
        description={r.description}
        keywords={[r.name.toLowerCase(), r.tag.toLowerCase() + ' indian recipes', 'healthy indian recipe']}
        image={r.image}
        path={`/recipes/${r.slug}`}
        jsonLd={jsonLd}
      />
      <Navbar onBook={() => setBookOpen(true)} />

      <article className="pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/recipes" className="text-sm text-[#7a8471] mb-6 inline-flex items-center gap-2 hover:text-[#3a5a2c]">
            <ArrowLeft className="w-4 h-4" /> All recipes
          </Link>

          <div className="flex items-center gap-3 mb-4 text-xs">
            <span className="tracking-widest uppercase text-[#c9743a] bg-[#fce7d5] px-2.5 py-1 rounded-full">{r.tag}</span>
            <span className="inline-flex items-center gap-1 text-[#7a8471]"><Clock className="w-3 h-3" /> {r.time}</span>
            <span className="inline-flex items-center gap-1 text-[#7a8471]"><Flame className="w-3 h-3" /> {r.calories} kcal</span>
            <span className="inline-flex items-center gap-1 text-[#7a8471]"><Users className="w-3 h-3" /> {r.servings} servings</span>
          </div>

          <h1 className="font-serif text-4xl md:text-5xl text-[#1a2b17] tracking-tight leading-[1.1]">{r.name}</h1>
          <p className="mt-5 text-lg text-[#4a5544] leading-relaxed">{r.description}</p>
        </div>

        <div className="max-w-4xl mx-auto px-6 mt-10">
          <div className="aspect-[16/9] rounded-3xl overflow-hidden shadow-lg">
            <img src={r.image} alt={r.name} className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 mt-14 grid md:grid-cols-2 gap-10">
          <section className="bg-white rounded-3xl p-7">
            <h2 className="font-serif text-2xl text-[#1a2b17] mb-5">Ingredients</h2>
            <ul className="space-y-3">
              {r.ingredients.map((ing, i) => (
                <li key={i} className="flex items-start gap-3 text-[#2a3a24]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#c9743a] mt-2.5 flex-shrink-0" />
                  <span>{ing}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="bg-white rounded-3xl p-7">
            <h2 className="font-serif text-2xl text-[#1a2b17] mb-5">Macros per serving</h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 rounded-2xl bg-[#faf7f1] text-center">
                <div className="text-[10px] tracking-widest uppercase text-[#7a8471]">Calories</div>
                <div className="font-serif text-2xl text-[#1a2b17] mt-1">{r.calories}</div>
              </div>
              <div className="p-4 rounded-2xl bg-[#faf7f1] text-center">
                <div className="text-[10px] tracking-widest uppercase text-[#7a8471]">Protein</div>
                <div className="font-serif text-2xl text-[#1a2b17] mt-1">{r.protein}g</div>
              </div>
              <div className="p-4 rounded-2xl bg-[#faf7f1] text-center">
                <div className="text-[10px] tracking-widest uppercase text-[#7a8471]">Time</div>
                <div className="font-serif text-2xl text-[#1a2b17] mt-1">{r.time.split(' ')[0]}</div>
              </div>
              <div className="p-4 rounded-2xl bg-[#faf7f1] text-center">
                <div className="text-[10px] tracking-widest uppercase text-[#7a8471]">Serves</div>
                <div className="font-serif text-2xl text-[#1a2b17] mt-1">{r.servings}</div>
              </div>
            </div>
          </section>
        </div>

        <div className="max-w-3xl mx-auto px-6 mt-10">
          <section>
            <h2 className="font-serif text-3xl text-[#1a2b17] mb-6">Method</h2>
            <ol className="space-y-5">
              {r.steps.map((s, i) => (
                <li key={i} className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#3a5a2c] text-white flex items-center justify-center font-medium text-sm flex-shrink-0">{i + 1}</div>
                  <div className="pt-1 text-[#2a3a24] leading-[1.75]">{s}</div>
                </li>
              ))}
            </ol>
          </section>

          {r.tips && r.tips.length > 0 && (
            <section className="mt-12 bg-white rounded-3xl p-8">
              <h3 className="font-serif text-2xl text-[#1a2b17] mb-5">Dietitian tips</h3>
              <ul className="space-y-3">
                {r.tips.map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#1a2b17]">
                    <CheckCircle2 className="w-5 h-5 text-[#3a5a2c] flex-shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="mt-12 p-8 md:p-10 rounded-3xl bg-gradient-to-br from-[#3a5a2c] to-[#4d6f3d] text-white">
            <div className="text-xs tracking-[0.2em] uppercase text-[#f4d5a8] mb-2">Want a personalised plan?</div>
            <h3 className="font-serif text-3xl">Get recipes built for you</h3>
            <p className="text-white/80 mt-3">A NutrifyMe dietitian will craft weekly meal plans around your goals, cravings and calendar.</p>
            <div className="mt-5 flex gap-3 flex-wrap">
              <Button onClick={() => setBookOpen(true)} className="rounded-full bg-white text-[#1a2b17] hover:bg-[#f4d5a8]">Book free call <ArrowRight className="w-4 h-4 ml-2" /></Button>
              <Button asChild variant="outline" className="rounded-full bg-transparent border-white/40 text-white hover:bg-white/10"><Link to="/quiz">Take diet quiz</Link></Button>
            </div>
          </div>
        </div>
      </article>

      <Footer onBook={() => setBookOpen(true)} />
      <BookingModal open={bookOpen} onClose={() => setBookOpen(false)} />
    </div>
  );
};
